<template>
    <div>
        <b-alert
            variant="warning"
            show
            class="app-alert--attention"
        >
            <div class="alert-body d-flex align-items-center">
                <feather-icon
                    icon="InfoIcon"
                    size="18"
                    class="mr-75 flex-shrink-0"
                    aria-hidden="true"
                />
                <span>
                    This file is used only to inspect the source structure and help build the manifest.
                    It is not stored in the manifest definition.
                </span>
            </div>
        </b-alert>

        <b-alert
            v-if="value.fromVersion"
            variant="secondary"
            show
            class="mb-2"
        >
            <div class="alert-body d-flex align-items-center">
                <feather-icon icon="CopyIcon" size="18" class="mr-75 flex-shrink-0" />
                <span>
                    Source rules and mapped column headers were loaded from
                    Revision {{ value.fromVersion.revision }}. You can edit the imported
                    configuration without uploading another representative file.
                </span>
            </div>
        </b-alert>

        <b-row>
            <b-col lg="8">
                <b-form-group label="Representative source file" label-for="manifest-source-file">
                    <b-form-file
                        id="manifest-source-file"
                        :value="value.file"
                        :disabled="disabled || uploading"
                        accept=".xlsx,.xls,.csv"
                        browse-text="Browse"
                        placeholder="Choose an XLSX, XLS, or CSV file"
                        @input="selectFile"
                    />
                </b-form-group>
            </b-col>
            <b-col lg="4" class="d-flex align-items-end">
                <b-button
                    class="mb-1"
                    variant="primary"
                    block
                    :disabled="disabled || uploading || !value.file"
                    @click="uploadAndInspect"
                >
                    <b-spinner v-if="uploading" small class="mr-50" />
                    {{ uploading ? 'Inspecting file' : 'Upload and inspect' }}
                </b-button>
            </b-col>
        </b-row>

        <template v-if="value.inspection">
            <div class="d-flex flex-wrap align-items-center mb-2">
                <template v-if="value.fromVersion">
                    <h5 class="mb-0 mr-1">Configured source structure</h5>
                    <b-badge variant="light-primary">
                        Revision {{ value.fromVersion.revision }}
                    </b-badge>
                </template>
                <template v-else>
                    <h5 class="mb-0 mr-1">{{ value.inspection.filename }}</h5>
                    <b-badge variant="light-primary" class="mr-50">
                        {{ value.inspection.fileType.toUpperCase() }}
                    </b-badge>
                    <span class="text-muted">{{ formatFileSize(value.inspection.size) }}</span>
                </template>
            </div>

            <b-row>
                <b-col md="6">
                    <b-form-group label="Sheet or tab" label-for="manifest-source-tab">
                        <b-form-select
                            id="manifest-source-tab"
                            :value="value.selectedTabIndex"
                            :options="tabOptions"
                            :disabled="disabled"
                            @input="selectTab"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Header row" label-for="manifest-header-row">
                        <b-form-input
                            id="manifest-header-row"
                            :value="value.headerRow"
                            :disabled="disabled"
                            type="number"
                            min="1"
                            @input="updateNumber('headerRow', $event)"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="First data row" label-for="manifest-data-start-row">
                        <b-form-input
                            id="manifest-data-start-row"
                            :value="value.dataStartRow"
                            :disabled="disabled"
                            type="number"
                            min="1"
                            @input="updateNumber('dataStartRow', $event)"
                        />
                    </b-form-group>
                </b-col>
            </b-row>

            <div v-if="hasSampleRows">
                <div class="d-flex justify-content-between align-items-center mb-1">
                    <h5 class="mb-0">Source preview</h5>
                    <span class="text-muted">
                        {{ currentTab.rowCount }} rows · {{ currentTab.columns.length }} columns
                    </span>
                </div>
                <b-table
                    small
                    bordered
                    responsive
                    :fields="previewFields"
                    :items="previewItems"
                    class="mb-0"
                />
            </div>
            <div v-else-if="value.fromVersion" class="manifest-source__preview-unavailable">
                <feather-icon icon="FileTextIcon" size="24" class="mb-50" />
                <div class="font-weight-bold">Sample rows are not stored in a manifest version</div>
                <small class="text-muted">
                    {{ currentTab ? currentTab.columns.length : 0 }} mapped source columns were restored.
                </small>
            </div>
        </template>
    </div>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BCol,
    BFormFile,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    BSpinner,
    BTable,
} from 'bootstrap-vue'
import {ImportManifestApi} from '@core/api'

export default {
    components: {
        BAlert,
        BBadge,
        BButton,
        BCol,
        BFormFile,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BRow,
        BSpinner,
        BTable,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            uploading: false,
        }
    },
    computed: {
        tabOptions() {
            if (!this.value.inspection) {
                return []
            }

            return this.value.inspection.tabs.map(tab => ({
                value: tab.index,
                text: this.value.fromVersion
                    ? tab.name
                    : `${tab.name} (${tab.rowCount} rows)`,
            }))
        },
        currentTab() {
            if (!this.value.inspection) {
                return null
            }

            return this.value.inspection.tabs.find(
                tab => tab.index === this.value.selectedTabIndex
            ) || null
        },
        hasSampleRows() {
            return Boolean(
                this.currentTab
                && Array.isArray(this.currentTab.sampleRows)
                && this.currentTab.sampleRows.length
            )
        },
        previewFields() {
            if (!this.currentTab) {
                return []
            }

            return [
                {key: '_row', label: 'Row', thClass: 'text-nowrap'},
                ...this.currentTab.columns.map(column => ({
                    key: `column_${column.index}`,
                    label: column.candidateHeader || `Column ${column.index + 1}`,
                })),
            ]
        },
        previewItems() {
            if (!this.currentTab) {
                return []
            }

            return this.hasSampleRows ? this.currentTab.sampleRows.map(sampleRow => {
                const item = {_row: sampleRow.row}
                sampleRow.cells.forEach((cell, index) => {
                    item[`column_${index}`] = cell
                })
                return item
            }) : []
        },
    },
    methods: {
        selectFile(file) {
            this.$emit('input', {
                ...this.value,
                file,
                uploadedFile: null,
                inspection: null,
                selectedTabIndex: null,
                headerRow: null,
                dataStartRow: null,
                selector: null,
                fromVersion: null,
            })
        },
        uploadAndInspect() {
            if (!this.value.file || this.uploading) {
                return
            }

            this.uploading = true
            let uploadedFile = null

            ImportManifestApi.uploadSourceFile(this.value.file)
                .then(response => {
                    uploadedFile = response.data
                    return ImportManifestApi.inspectSource(uploadedFile.fileReferenceId)
                })
                .then(response => {
                    const inspection = response.data
                    const firstTab = inspection.tabs.length ? inspection.tabs[0] : null
                    const headerRow = firstTab ? firstTab.candidateHeaderRow : null

                    this.$emit('input', {
                        ...this.value,
                        uploadedFile,
                        inspection,
                        selectedTabIndex: firstTab ? firstTab.index : null,
                        headerRow,
                        dataStartRow: headerRow ? headerRow + 1 : null,
                        selector: null,
                        fromVersion: null,
                    })
                })
                .catch(error => {
                    this.$emit('error', error)
                })
                .finally(() => {
                    this.uploading = false
                })
        },
        selectTab(tabIndex) {
            const tab = this.value.inspection.tabs.find(item => item.index === tabIndex)
            const headerRow = tab ? tab.candidateHeaderRow : null

            this.$emit('input', {
                ...this.value,
                selectedTabIndex: tabIndex,
                headerRow,
                dataStartRow: headerRow ? headerRow + 1 : null,
                selector: null,
                fromVersion: null,
            })
        },
        updateNumber(property, number) {
            this.$emit('input', {
                ...this.value,
                [property]: number ? Number(number) : null,
            })
        },
        formatFileSize(bytes) {
            if (!bytes) {
                return '0 B'
            }

            if (bytes < 1024) {
                return `${bytes} B`
            }
            if (bytes < 1024 * 1024) {
                return `${(bytes / 1024).toFixed(1)} KB`
            }

            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
        },
    },
}
</script>

<style lang="scss" scoped>
.manifest-source__preview-unavailable {
    padding: 1.5rem;
    border: 1px dashed #d8d6de;
    border-radius: .357rem;
    color: #82868b;
    text-align: center;
}
</style>
